import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {findDOMNode} from 'react-dom';
import classNames from 'classnames';
import Base from '../Base/Base';
import {FormInput} from '@homeaway/react-form-components';
import determineIfMobile from '@homeaway/react-determine-if-mobile';
import emitGuestPickerEvent from '../../utils/edap';
import debounce from '../../utils/debounce';

/**
 * Guest Picker
 * wraps guest fields
*/

const keyChangeMap = {
    adultsCount: 'adults',
    childrenCount: 'children'
};

class GuestPicker extends Component {

    static propTypes = {
        /** Add content to the left of the input. This can be a string, element, or component */
        inputIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.func]),
        /** Left/right alignment of icon in input field */
        positionIconRight: PropTypes.bool,
        /** Guest Label */
        guestLabel: PropTypes.string,
        /** Form input's label. This can be a string, element, or component */
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.func]).isRequired,
        /** Form input's name property */
        name: PropTypes.string.isRequired,
        /** Form input's error message */
        errorMsg: PropTypes.node,
        /** Form input Display-only mode */
        readOnly: PropTypes.bool,
        /** By default, the input element is given a data-wdio attribute of "guest-picker-input".
        If you have more than one on a page you can add a prefix by using this property. */
        wdioPrefix: PropTypes.string,
        /** Number of Adults */
        adultsCount: PropTypes.number,
        /** Label for Adults */
        adultsLabel: PropTypes.string,
        /** Max Number of Adults */
        adultsMax: PropTypes.number,
        /** Min Number of Adults */
        adultsMin: PropTypes.number,
        /** Number of Children */
        childrenCount: PropTypes.number,
        /** Label for Children */
        childrenLabel: PropTypes.string,
        /** Max Number of Children */
        childrenMax: PropTypes.number,
        /** Min Number of Children */
        childrenMin: PropTypes.number,
        /** pet Checked false by default */
        petIncluded: PropTypes.bool,
        /** Label for pet Yes */
        petLabel: PropTypes.string,
        /** Label for pet Yes */
        petYesLabel: PropTypes.string,
        /** Label for pet No */
        petNoLabel: PropTypes.string,
        /** Show pet Radio Group false by default */
        showPetOption: PropTypes.bool,
        /** Show continue button on mobile */
        showContinue: PropTypes.bool,
        /** Disable apply/continue button on desktop/mobile (if shown) */
        disableButton: PropTypes.bool,
        /** Continue Button Label */
        continueButtonLabel: PropTypes.string,
        /** Clear Button Label */
        clearButtonLabel: PropTypes.string,
        /** Show Clear Button */
        showClearButton: PropTypes.bool,
        /** Apply Button Label */
        applyButtonLabel: PropTypes.string,
        /** Function to call when a guest picker is closed */
        onClose: PropTypes.func,
        /** Function to call when a guest picker changes */
        onChange: PropTypes.func,
        /** Function to call when a guest picker is cleared */
        onClear: PropTypes.func,
        /** Prevents keyboard input. Useful on iPad to prevent software keyboard from popping up. */
        disableTextInput: PropTypes.bool,
        /** State of of the modal (defaults to false) */
        isOpen: PropTypes.bool,
        /** Callback for when the guest picker opens */
        onOpen: PropTypes.func,
        /** Where should the tooltip be displayed (defaults to "top").
        Supports "top", "bottom", "right", "left". Checkout the react-tooltip
        documentation for more information */
        placement: PropTypes.oneOf(['top', 'left', 'bottom', 'right']),
        /** Centers tooltip position (defaults to true) */
        centered: PropTypes.bool,
        /** Duration of the fade transition when the tooltip closes in milliseconds */
        fadeOutDuration: PropTypes.number,
        /** Element to render above +/- buttons; can be string, element, or component */
        formPrefixElement: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.func]),
        /** Element to render below +/- buttons; can be string, element, or component */
        formSuffixElement: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.func]),
        /** Title of the modal */
        modalTitle: PropTypes.string,
        /** FormInput's id, required for a11y */
        id: PropTypes.string.isRequired
    }

    static defaultProps = {
        inputIcon: null,
        positionIconRight: false,
        guestLabel: 'Guest(s)',
        clearButtonLabel: 'Clear',
        showClearButton: true,
        continueButtonLabel: 'Continue',
        applyButtonLabel: 'Apply',
        modalTitle: 'Select guests',
        petLabel: 'pets',
        onClose: () => {},
        onChange: () => {},
        onClear: () => {},
        onExit: () => {},
        onOpen: () => {},
        showContinue: true,
        disableButton: false,
        readOnly: false,
        disableTextInput: false,
        isOpen: false,
        placement: 'top',
        centered: true,
        fadeOutDuration: 400,
        formPrefixElement: null,
        formSuffixElement: null
    }

    constructor(props) {
        super(props);
        this.state = {
            isOpen: props.isOpen,
            undoState: {}
        };

        // debounce for desktop (see TODO on line 146)
        this.handleDebounceFocus = debounce(() => {
            emitGuestPickerEvent({
                eventaction: 'popover shown',
                eventcategory: 'guest selector'
            });
        }, 0).bind(this);

        this.handleWindowClick = debounce(this.handleWindowClick.bind(this), 0, true);
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.isOpen !== nextProps.isOpen && this.state.isOpen !== nextProps.isOpen) {
            if (nextProps.isOpen) {
                this.handleFocus();
            } else {
                // call handleExit not handleClose since we don't want to confirm the changes
                this.handleExit();
            }
        }
    }

    componentDidUpdate() {
        // TODO: jank check
        if (this.props.isMobile && this.state.isDelayed) {
            window.setTimeout(() => {
                this.setState({isDelayed: false});
            }, 50);
        }
    }

    handleKeyPress = (props) => {
        if (props.key === 'Enter' || props.key === ' ') { // ' ' - for Spacebar
            this.handleFocus(props);
            props.preventDefault(); // prevents spacebar scrolling
        }
    }

    /* eslint-disable complexity */
    handleFocus = (eventProps) => {
        if (this.props.isMobile) {
            // Open, but set isDelayed to true so the component waits a moment to render
            this.setState({
                isOpen: true,
                isDelayed: true,
                undoState: {
                    adultsCount: this.props.adultsCount,
                    childrenCount: this.props.childrenCount,
                    petIncluded: this.props.petIncluded
                }
            }, () => {
                this.props.onOpen();
            });

            emitGuestPickerEvent({
                eventaction: 'popover shown',
                eventcategory: 'guest selector'
            });
        } else {
            if (!this.state.isOpen) {
                this.handleDebounceFocus();
            }

            const guestInput = findDOMNode(this).querySelector('input');

            // prevent double setState when guestInput receives focus;
            if (document && document.activeElement !== guestInput || eventProps.type === 'keydown') {
                // as we may be clicking on the overlay, rather than tabbing to the input
                // force focus of the input for text entry (text entry is TODO)
                if (!this.props.disableTextInput) {
                    guestInput.focus();
                }
                this.setState({
                    isOpen: true,
                    undoState: {
                        adultsCount: this.props.adultsCount,
                        childrenCount: this.props.childrenCount,
                        petIncluded: this.props.petIncluded
                    }
                }, () => {
                    this.props.onOpen();
                });

                // Listen for clicks outside of the popup and the input
                window.setTimeout(() => {
                    window.addEventListener('click', this.handleWindowClick);
                    document.documentElement.addEventListener('mouseup', this.handleWindowClick);
                    document.documentElement.addEventListener('touchstart', this.handleWindowClick);
                }, 0);
            }
        }
    }

    handleChange = (key, value) => {
        if (keyChangeMap[key]) {
            emitGuestPickerEvent({
                eventaction: 'counter button click',
                eventcategory: 'guest selector',
                eventlabel: keyChangeMap[key]
            });
        }
        this.props.onChange(key, value);
    }

    handleClear = (event) => {
        emitGuestPickerEvent({
            eventaction: 'popover clear',
            eventcategory: 'guest selector'
        });
        event.preventDefault();
        this.props.onClear();
    }

    handleClose = () => {
        if (this.state.isOpen) {
            emitGuestPickerEvent({
                eventaction: 'popover hidden',
                eventcategory: 'guest selector'
            });
            /* We need to make sure the state is updated before calling onClose. If it is not,
            before componentWillReceiveProps will then call onExit if it is controlled by the parent since
            the parent re-updates the state */
            this.setState({isOpen: false}, () => {
                this.props.onClose();
            });
        }
    }

    handleExit = () => {
        if (this.state.isOpen) {
            emitGuestPickerEvent({
                eventaction: 'popover hidden',
                eventcategory: 'guest selector'
            });

            this.setState({isOpen: false}, () => {
                this.props.onExit(this.state.undoState);
            });
        }
    }

    contains(elem, target) {
        return elem && findDOMNode(elem).contains(target);
    }

    handleWindowClick = (event) => {
        // We want to close the tooltip when the user clicks anything on the screen,
        // outside of the tooltip. This is for non-mobile.
        const isWithin = (
            this.contains(this.input, event.target) ||
            this.contains(this.guestSelector, event.target) ||
            this.contains(this.overlay, event.target)
        );

        if (isWithin) {
            event.stopImmediatePropagation();
        } else {
            // Stop listening for clicks outside of the tooltip's parent element
            window.setTimeout(() => {
                window.removeEventListener('click', this.handleWindowClick);
                document.documentElement.removeEventListener('mouseup', this.handleWindowClick);
                document.documentElement.removeEventListener('touchstart', this.handleWindowClick);
            }, 0);
        }
    }

    /**
     * compRefFun
     * Used to compare against window for onClose
     */
    compRefFun = (c) => {
        this.guestSelector = c;
    }

    // eslint-disable-next-line complexity
    renderInput() {
        const {
            label,
            name,
            guestLabel,
            errorMsg,
            className,
            readOnly,
            wdioPrefix,
            inputIcon,
            positionIconRight,
            id
        } = this.props;

        const inputClasses = classNames(className, {
            'not-empty': this.state.isOpen,
            'focus': this.state.isOpen
        });

        const overlayClasses = classNames('picker__overlay', {
            'picker__overlay--selectable': !readOnly
        });

        return (
            <div className="position-relative">
                <FormInput
                    className={inputClasses}
                    name={name}
                    label={label}
                    value={guestLabel}
                    onChange={this.handleChangeInput}
                    onFocus={readOnly ? null : this.handleFocus}
                    onKeyDown={readOnly ? null : this.handleKeyPress}
                    errorMsg={errorMsg}
                    ref={(c) => (this.input = c)} //eslint-disable-line
                    readOnly={'readOnly'}
                    data-wdio={wdioPrefix ? `${wdioPrefix}-guest-picker-input` : 'guest-picker-input'}
                    leftContent={!positionIconRight ? inputIcon : null}
                    rightContent={positionIconRight ? inputIcon : null}
                    id={`input-${id}`}
                    role="combobox"
                    aria-haspopup={'true'}
                    aria-expanded={this.state.isOpen}
                />
                <div
                    className={overlayClasses}
                    ref={(c) => (this.overlay = c)} //eslint-disable-line
                    onClick={readOnly ? null : this.handleFocus}
                    onKeyDown={readOnly ? null : this.handleKeyPress}
                />
            </div>
        );
    }

    render() {
        const classes = classNames(this.props.className, 'guest-picker');
        return (
            <Base
                {...this.props}
                className={classes}
                inputs={this.renderInput()}
                fadeOutDuration={this.props.fadeOutDuration}
                isMobile={this.props.isMobile}
                isOpen={this.state.isOpen}
                onModalClose={this.handleExit}
                compRefFun={this.compRefFun}
                onChange={this.handleChange}
                onClear={this.handleClear}
                onApply={this.handleClose}
                onClose={this.handleExit}
                formPrefixElement={this.props.formPrefixElement}
                formSuffixElement={this.props.formSuffixElement}
            />
        );
    }
}

export default determineIfMobile(GuestPicker);
